/**
 * Box 5 of the ICS 208HM-CG, the product or material description table.
 *
 * Ten narrow columns do not survive being edited in place, so a row is read in
 * the table and written in a dialog. The list is held on the hazmat record as a
 * jsonb array rather than as rows of its own, which is why editing goes through
 * the card's draft instead of a mutation hook.
 */

import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Material } from 'components/IAP/hooks/useSafetyAnalysis';
import generateUUID from 'utils/sharedUtils/generateUUID';

import {
  DIALOG_SURFACE,
  ICS204_TABLE,
} from '../AssignmentList/assignmentCardStyles';
import { ICS208HM_MATERIAL_COLUMNS } from '../ics208hmBoxLabels';

type MaterialListProps = {
  material?: Material[];
  updateMaterials: (material: Material[]) => void;
  readOnly?: boolean;
};

const emptyMaterial = (): Material => ({
  id: generateUUID(),
  materialName: '',
  quantity: null,
  physState: '',
  niosh: '',
  specificGravity: '',
  ph: null,
  idlh: '',
  flashPoint: '',
  lel: '',
  uel: '',
});

/** Quantity and pH are numbers on the record and free text in the dialog. */
const NUMERIC_FIELDS = new Set(['quantity', 'ph']);

const cellText = (material: Material, field: string): string => {
  const value = (material as unknown as Record<string, unknown>)[field];
  return value === null || value === undefined || value === ''
    ? '—'
    : String(value);
};

const MaterialList: React.FC<MaterialListProps> = ({
  material = [],
  updateMaterials,
  readOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Material | null>(null);
  // Whether the open dialog is adding a row or editing one already on the list.
  const [isNew, setIsNew] = useState(false);

  const filteredMaterials = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return material;
    return material.filter(
      (entry) =>
        entry?.materialName?.toLowerCase().includes(term) ||
        entry?.niosh?.toLowerCase().includes(term)
    );
  }, [material, search]);

  const commitDraft = () => {
    if (!draft) return;
    updateMaterials(
      isNew
        ? [...material, draft]
        : material.map((entry) => (entry.id === draft.id ? draft : entry))
    );
    setDraft(null);
  };

  const setDraftField = (field: string, value: string) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            [field]: NUMERIC_FIELDS.has(field)
              ? value === ''
                ? null
                : Number(value)
              : value,
          }
        : current
    );

  const columnCount = ICS208HM_MATERIAL_COLUMNS.length + (readOnly ? 0 : 1);

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label="Search materials"
          placeholder="Search product / material..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            onClick={() => {
              setDraft(emptyMaterial());
              setIsNew(true);
            }}
          >
            <Plus className="tw:size-4" />
            Add Material
          </Button>
        )}
      </div>

      <div className="tw:overflow-x-auto">
        <Table className={ICS204_TABLE} data-testid="ics208hm-materials">
          <TableHeader>
            <TableRow>
              {ICS208HM_MATERIAL_COLUMNS.map((column) => (
                <TableHead key={column.field}>{column.label}</TableHead>
              ))}
              {!readOnly && <TableHead className="tw:w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMaterials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="tw:text-center">
                  {search ? 'No results!' : 'No materials described.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredMaterials.map((entry) => {
                const rowLabel = entry.materialName || 'this material';
                return (
                  <TableRow key={entry.id}>
                    {ICS208HM_MATERIAL_COLUMNS.map((column) => (
                      <TableCell key={column.field}>
                        {cellText(entry, column.field)}
                      </TableCell>
                    ))}
                    {!readOnly && (
                      <TableCell>
                        <div className="tw:flex tw:justify-end tw:gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${rowLabel}`}
                            onClick={() => {
                              setDraft({ ...entry });
                              setIsNew(false);
                            }}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${rowLabel}`}
                            onClick={() =>
                              updateMaterials(
                                material.filter((m) => m.id !== entry.id)
                              )
                            }
                          >
                            <Trash2 className="tw:size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <span className="tw:text-sm tw:text-muted-foreground">
        {filteredMaterials.length}{' '}
        {filteredMaterials.length === 1 ? 'Material' : 'Materials'}
      </span>

      <Dialog
        open={draft != null}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
      >
        <DialogContent className={DIALOG_SURFACE}>
          <DialogHeader>
            <DialogTitle>
              {isNew ? 'Add material' : 'Edit material'}
            </DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
              {ICS208HM_MATERIAL_COLUMNS.map((column) => {
                const fieldId = `ics208hm-material-${column.field}`;
                const value = (draft as unknown as Record<string, unknown>)[
                  column.field
                ];
                return (
                  <Field key={column.field} className="tw:gap-2">
                    <FieldLabel htmlFor={fieldId}>{column.label}</FieldLabel>
                    <Input
                      id={fieldId}
                      type={
                        NUMERIC_FIELDS.has(column.field) ? 'number' : 'text'
                      }
                      value={
                        value === null || value === undefined
                          ? ''
                          : String(value)
                      }
                      onChange={(event) =>
                        setDraftField(column.field, event.target.value)
                      }
                    />
                  </Field>
                );
              })}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose
              render={
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              }
            />
            <Button
              type="button"
              disabled={!draft?.materialName?.trim()}
              onClick={commitDraft}
            >
              {isNew ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialList;
